import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';

import type { RegistryOrderSubject } from '@/hooks/useRegistryOrderSubjects';
import type { RegistryCompanyCandidate } from '@/types/registry-documents';
import { COUNTRY_OPTIONS, type CompanyMode } from './orderDraft';
import type { OrderCompany } from './useOrderCompany';

interface CompanyStepProps {
  company: OrderCompany;
  subjects: RegistryOrderSubject[];
  subjectsLoading: boolean;
  subjectsError: Error | null;
}

/** Step 1 — who the documents are for. */
export function CompanyStep({
  company,
  subjects,
  subjectsLoading,
  subjectsError,
}: Readonly<CompanyStepProps>) {
  const subjectOptions = subjects.map((entry) => ({ value: entry.id, label: entry.name }));
  return (
    <Stack gap="sm" mt="sm">
      <Group grow align="flex-start">
        <Select
          label="Subject"
          description="Prefills the search from the report's company subject"
          placeholder={subjectsLoading ? 'Loading subjects…' : 'No company subject'}
          data={subjectOptions}
          value={company.subjectId}
          onChange={company.setSubjectId}
          clearable
          disabled={subjectsLoading || subjects.length === 0}
          rightSection={subjectsLoading ? <Loader size="xs" /> : undefined}
          error={
            subjectsError
              ? `Subjects could not be loaded (${subjectsError.message}) — fill the company in by hand`
              : undefined
          }
          comboboxProps={{ withinPortal: false }}
          data-testid="registry-order-subject"
        />
        <Select
          label="Country"
          description="The registry the documents come from"
          placeholder="Pick a country"
          data={COUNTRY_OPTIONS}
          value={company.country}
          onChange={company.setCountry}
          searchable
          required
          comboboxProps={{ withinPortal: false }}
          data-testid="registry-order-country"
        />
      </Group>

      <SegmentedControl
        value={company.mode}
        onChange={(value) => company.setMode(value as CompanyMode)}
        data={[
          { value: 'search', label: 'Search Armadillo' },
          { value: 'manual', label: 'Company not found' },
        ]}
        data-testid="registry-order-mode"
      />

      {company.mode === 'search' ? (
        <CompanySearch company={company} />
      ) : (
        <ManualCompany company={company} />
      )}
    </Stack>
  );
}

function CompanySearch({ company }: Readonly<{ company: OrderCompany }>) {
  const { search } = company;
  const noMatch = search.hasSearched && !search.error && search.candidates.length === 0;
  return (
    <Stack gap="xs">
      <Group align="flex-end" grow>
        <TextInput
          label="Company name"
          value={company.searchName}
          onChange={(event) => company.setSearchName(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && company.canSearch) {
              event.preventDefault();
              company.runSearch();
            }
          }}
          data-testid="registry-order-search-name"
        />
        <TextInput
          label="Registration number"
          description="Optional — an exact match beats a name search"
          value={company.searchRegistrationNumber}
          onChange={(event) => company.setSearchRegistrationNumber(event.currentTarget.value)}
          data-testid="registry-order-search-registration"
        />
      </Group>
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          Searching is free; only an order costs anything.
        </Text>
        <Button
          size="xs"
          variant="light"
          onClick={company.runSearch}
          disabled={!company.canSearch}
          loading={search.isSearching}
          data-testid="registry-order-search-btn"
        >
          Search
        </Button>
      </Group>

      {search.error && (
        <Alert color="red" title="Search failed">
          {search.error.message} Try again, or switch to “Company not found” to order by name.
        </Alert>
      )}

      {noMatch && (
        <Alert color="gray" title="No match in Armadillo's index">
          Try the registration number, or switch to “Company not found” to order by name — their
          research desk identifies the company from the details you give.
        </Alert>
      )}

      {search.candidates.length > 0 && (
        <CandidateTable
          candidates={search.candidates}
          matchedResults={search.matchedResults}
          selected={company.candidate}
          onSelect={company.setCandidate}
        />
      )}
    </Stack>
  );
}

function ManualCompany({ company }: Readonly<{ company: OrderCompany }>) {
  const { manual } = company;
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Armadillo's research desk finds the company from these details; they go with the order as a
        note. The name and country are enough, the rest helps.
      </Text>
      <TextInput
        label="Company name"
        required
        {...manual.getInputProps('companyName')}
        data-testid="registry-order-manual-name"
      />
      <Group grow>
        <TextInput
          label="Registration number"
          {...manual.getInputProps('registrationNumber')}
          data-testid="registry-order-manual-registration"
        />
        <TextInput label="Postcode" {...manual.getInputProps('postcode')} />
      </Group>
      <TextInput label="Address" {...manual.getInputProps('address')} />
      <TextInput label="Town" {...manual.getInputProps('town')} />
    </Stack>
  );
}

function CandidateTable({
  candidates,
  matchedResults,
  selected,
  onSelect,
}: Readonly<{
  candidates: RegistryCompanyCandidate[];
  matchedResults: number | null;
  selected: RegistryCompanyCandidate | null;
  onSelect: (candidate: RegistryCompanyCandidate) => void;
}>) {
  const truncated = matchedResults != null && matchedResults > candidates.length;
  return (
    <Stack gap={4}>
      {truncated && (
        <Text size="xs" c="dimmed">
          Showing {candidates.length} of {matchedResults} matches — narrow the name or add the
          registration number.
        </Text>
      )}
      <Table highlightOnHover withTableBorder verticalSpacing="xs" fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={36} />
            <Table.Th>Company</Table.Th>
            <Table.Th>Address</Table.Th>
            <Table.Th>Registration</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {candidates.map((entry) => (
            <CandidateRow
              key={entry.companyId}
              entry={entry}
              checked={selected?.companyId === entry.companyId}
              onSelect={() => onSelect(entry)}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function CandidateRow({
  entry,
  checked,
  onSelect,
}: Readonly<{ entry: RegistryCompanyCandidate; checked: boolean; onSelect: () => void }>) {
  return (
    <Table.Tr
      onClick={onSelect}
      style={{ cursor: 'pointer' }}
      data-testid={`registry-candidate-${entry.companyId}`}
    >
      <Table.Td>
        <Radio checked={checked} onChange={onSelect} aria-label={`Select ${entry.name}`} />
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="wrap">
          <Text size="sm">{entry.name}</Text>
          {entry.isBranch && (
            <Badge size="xs" variant="light" color="gray">
              branch
            </Badge>
          )}
          {entry.isOutOfBusiness && (
            <Badge size="xs" variant="light" color="red">
              out of business
            </Badge>
          )}
          {entry.sourceArm !== 'name' && (
            <Tooltip label={`Matched on ${entry.sourceArm.replace('_', ' ')}`}>
              <Badge size="xs" variant="light" color="green">
                exact
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {entry.address ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {entry.registrationNumbers.length ? entry.registrationNumbers.join(', ') : '—'}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}
